import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  MenuItem,
} from "@mui/material";
import { Col, Row } from "antd";
import { Container } from "react-bootstrap";
import SaveIcon from "@mui/icons-material/Save";
import DescriptionIcon from "@mui/icons-material/Description";
import toast from "react-hot-toast";
import axios from "axios";
import { URL } from "../config";
import { useNavigate } from "react-router-dom";

const NewLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
    role: "admin",
  });
  const [errors, setErrors] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const navigate = useNavigate();
  const nameRef = useRef();

  
  useEffect(() => {
    if (loginData.role === "doctor") {
      fetchDoctors();
    }
  }, [loginData.role]);

  const fetchDoctors = async () => {
    try {
      setLoadingDoctors(true);
      const res = await axios.get(`${URL}/doctormasters`);
      const data = res.data.map((doc) => ({
        id: doc.DoctorId,
        name: doc.DoctorName,
      }));
      setDoctors(data);
    } catch (err) {
      console.error("Error fetching doctors:", err);
      toast.error("Failed to load doctors");
    } finally {
      setLoadingDoctors(false);
    }
  };

 
  const handleDoctorSelect = (e) => {
    const doctorId = e.target.value;
    const doctor = doctors.find((d) => d.id === doctorId);
    setSelectedDoctor(doctorId);
    setLoginData((prev) => ({
      ...prev,
      username: doctor ? doctor.name : "",
    }));
  };

  const handleChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    setErrors({});
  };

  const validateFields = () => {
    let temp = {};
    if (loginData.role === "doctor" && !selectedDoctor) {
      temp.doctor = "Please select a doctor";
    }
    if (!loginData.username.trim()) temp.username = "Username is required";
    if (!loginData.password.trim()) temp.password = "Password is required";
    if (!loginData.role) temp.role = "Role is required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

const handleSave = async () => {
  if (!validateFields()) return;
  setIsLoading(true);
  try {
    let payload = {
      username: loginData.username.trim(),
      password: loginData.password.trim(),
      role: loginData.role.trim(),
    };

    
    if (loginData.role === "doctor") {
      const doctor = doctors.find((d) => d.id === selectedDoctor);
      if (!doctor) {
        toast.error("Selected doctor not found!");
        setIsLoading(false);
        return;
      }
      payload.username = doctor.name; 
      payload.fkDoctorId = doctor.id; 
    }

    console.log("➡️ Sending payload:", payload);

    await axios.post(`${URL}/signup`, payload, {
      headers: { "Content-Type": "application/json" },
    });

    toast.success("Login saved successfully!");
    navigate("/dashboard/user-access-login-details");
  } catch (err) {
    console.error(" Signup error:", err.response?.data || err.message);
    toast.error(err.response?.data?.error?.message || "Failed to save login");
  } finally {
    setIsLoading(false);
  }
};


  const fieldStyle = (theme) => ({
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      "& fieldset": { borderColor: "#c4c4c4", borderWidth: "1px" },
      "&:hover fieldset": { borderColor: "#070707ff", borderWidth: "1px" },
      "&.Mui-focused fieldset": { borderColor: "#0d0d0dff", borderWidth: "1px" },
    },
    "& .MuiInputBase-root": { minHeight: "28px", fontSize: "0.85rem" },
    "& .MuiInputLabel-root": {
      color: "#037589ff",
      fontWeight: 600,
      fontSize: "14px",
      [theme.breakpoints.up("sm")]: { fontSize: "14px" },
      [theme.breakpoints.up("md")]: { fontSize: "16px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#037589ff !important" },
    "& .MuiInputLabel-asterisk": { color: "red" },
  });

  return (
    <Container fluid className="mainpaper myForm mt-3 my-5">
      <Paper
        elevation={7}
        sx={{
          padding: { xs: "8px 0", sm: "10px 0" },
          borderRadius: 2,
          mt: { xs: 2, sm: 4 },
          maxWidth: { xs: "95%", sm: "90%", md: "100%" },
          margin: { xs: "10px", sm: "20px" },
        }}
      >
        <div className="head-bg" style={{ marginTop: "10px", marginBottom: "15px" }}>
          <h4 className="heading">New Login Form</h4>
        </div>

        <Divider sx={{ borderWidth: 1, borderColor: "#037589ff", my: 1 }} />

        <Box
          component="form"
          sx={{
            "& .MuiTextField-root": {
              m: "0.1rem",
              width: { xs: "95%", sm: "90%", md: "85%" },
            },
            "& .MuiInputBase-root": { fontSize: "0.75rem" },
            padding: { xs: 1, sm: 2 },
            margin: "auto",
          }}
          noValidate
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
           
            <Col xl={8} md={12} sm={24} xs={24}>
              <TextField
                label="Username"
                name="username"
                required
                fullWidth
                size="small"
                value={loginData.username}
                onChange={handleChange}
                inputRef={nameRef}
                InputLabelProps={{ shrink: true }}
                sx={(theme) => fieldStyle(theme)}
                error={!!errors.username}
                helperText={errors.username}
                disabled={loginData.role === "doctor"}
              />
            </Col>

          
            <Col xl={8} md={12} sm={24} xs={24}>
              <TextField
                label="Password"
                name="password"
                type="password"
                required
                fullWidth
                size="small"
                value={loginData.password}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={(theme) => fieldStyle(theme)}
                error={!!errors.password}
                helperText={errors.password}
              />
            </Col>

            
            <Col xl={8} md={12} sm={24} xs={24}>
              <FormLabel
                sx={{
                  color: "#037589ff",
                  fontWeight: 600,
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.85rem",
                }}
              >
                Role
              </FormLabel>

              <RadioGroup
                row
                name="role"
                value={loginData.role}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="admin"
                  control={<Radio sx={{ transform: "scale(0.8)" }} />}
                  label="Admin"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                />
                <FormControlLabel
                  value="doctor"
                  control={<Radio sx={{ transform: "scale(0.8)" }} />}
                  label="Doctor"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                />
                <FormControlLabel
                  value="receptionist"
                  control={<Radio sx={{ transform: "scale(0.8)" }} />}
                  label="Receptionist"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                />
              </RadioGroup>

              {errors.role && (
                <p style={{ color: "red", fontSize: "0.75rem", marginTop: 2 }}>
                  {errors.role}
                </p>
              )}
            </Col>

          
            {loginData.role === "doctor" && (
              <Col xl={8} md={12} sm={24} xs={24}>
                <TextField
                  select
                  label="Select Doctor"
                  size="small"
                  fullWidth
                  value={selectedDoctor}
                  onChange={handleDoctorSelect}
                  InputLabelProps={{ shrink: true }}
                  sx={(theme) => fieldStyle(theme)}
                  error={!!errors.doctor}
                  helperText={errors.doctor}
                >
                  {loadingDoctors ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : (
                    doctors.map((doc) => (
                      <MenuItem key={doc.id} value={doc.id}>
                        {doc.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Col>
            )}
          </Row>
        </Box>

      
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2 }}
          padding={{ xs: "10px 10px", sm: "14px 20px" }}
          justifyContent="end"
          alignItems="center"
        >
          <Button
            variant="contained"
            startIcon={
              isLoading ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />
            }
            onClick={handleSave}
            sx={{
              backgroundColor: "#037589ff",
              color: "#fff",
              fontWeight: 600,
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.7rem", sm: "0.875rem" },
              "&:hover": { backgroundColor: "#037589ff" },
            }}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>

          <Button
            variant="contained"
            startIcon={<DescriptionIcon fontSize="small" />}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.7rem", sm: "0.875rem" },
              ml: { xs: 1, sm: 2 },
              backgroundColor: "#999799ff",
            }}
            onClick={() => navigate("/dashboard/user-access-login-details")}
          >
            Details
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default NewLogin;
